import { useEffect, useState, useMemo } from "react";
import { useSearchParams } from "react-router-dom";
import { api, API_BASE, getToken } from "@/lib/api";
import { useBrands } from "@/contexts/BrandContext";
import { useCategories } from "@/hooks/useCategories";
import { ScoreBadge } from "@/components/app/ScoreBadge";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Search, Filter, X, SlidersHorizontal, Download, Bookmark, BookmarkPlus,
  Sparkles, Trash2, ChevronDown, Ban, RotateCcw,
} from "lucide-react";
import { LabelWithTip } from "@/components/app/InfoTip";
import { MultiSelect } from "@/components/app/MultiSelect";
import { RangeSlider } from "@/components/app/RangeSlider";
import {
  DropdownMenu, DropdownMenuContent, DropdownMenuItem,
  DropdownMenuTrigger, DropdownMenuLabel, DropdownMenuSeparator,
} from "@/components/ui/dropdown-menu";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from "@/components/ui/dialog";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";

const QUICK_FILTERS = [
  ["new", "New"],
  ["rising", "Rising"],
  ["declining", "Declining"],
  ["gap", "Product gaps"],
  ["high", "High opportunity"],
];

const TERM_LENGTH_OPTIONS = [
  { value: "single", label: "1 word" },
  { value: "double", label: "2 words" },
  { value: "longtail", label: "Long-tail (3+)" },
];

const RANK_BANDS = [
  ["", "All ranks"],
  ["100", "Top 100"],
  ["500", "Top 500"],
  ["1000", "Top 1k"],
  ["5000", "Top 5k"],
];

// Smart presets — each writes a known set of URL params (`patch()` fields).
// Keep these tuned to real Merchandising playbooks so they actually drive action.
const SMART_PRESETS = [
  {
    key: "quick_wins",
    label: "🚀 Quick Wins",
    description: "High opportunity, already matched to a SKU, top 500 rank",
    params: { opp: "70,100", matched: "yes", rank: "500", chip: null },
  },
  {
    key: "growth_gaps",
    label: "🧪 Growth Gaps",
    description: "Detected product gaps with strong demand in top 1k",
    params: { chip: "gap", rank: "1000", dem: "60,100", opp: null, matched: null },
  },
  {
    key: "rising_stars",
    label: "📈 Rising Stars",
    description: "Rapidly improving rank + opportunity ≥ 50",
    params: { chip: "rising", opp: "50,100", matched: null, rank: null },
  },
  {
    key: "defend_brand",
    label: "🛡️ Defend Brand",
    description: "Competitor brands present + matched SKU = defensive bidding",
    params: { has_comp: "yes", matched: "yes", chip: null, rank: "1000" },
  },
  {
    key: "high_traffic_low_relevance",
    label: "🎯 Misaligned Traffic",
    description: "High demand but low relevance — listing optimization wins",
    params: { dem: "70,100", rel: "0,40", matched: "yes", chip: null },
  },
];

// ---- URL <-> state helpers ----
const numList = (s) => (s ? s.split(",").filter(Boolean) : []);
const numRange = (s) => {
  if (!s) return null;
  const [lo, hi] = s.split(",").map(Number);
  return Number.isFinite(lo) && Number.isFinite(hi) ? [lo, hi] : null;
};

export default function SearchTerms() {
  const { selectedBrandId } = useBrands();
  const { categories: catMaster } = useCategories();
  const [data, setData] = useState({ items: [], total: 0 });
  const [facets, setFacets] = useState({ clusters: [], categories: [], subcategories: [], actions: [] });
  const [loading, setLoading] = useState(false);
  const [showAdvanced, setShowAdvanced] = useState(false);

  const [searchParams, setSearchParams] = useSearchParams();

  // ---- Filter state (synced to URL) ----
  const search = searchParams.get("q") || "";
  const filter = searchParams.get("chip") || "all";
  const sort = {
    by: searchParams.get("sort_by") || "opportunity_score",
    order: searchParams.get("order") || "desc",
  };
  const page = Math.max(1, Number(searchParams.get("page") || 1));
  const PAGE_SIZE = 100;

  const clusterIds = numList(searchParams.get("clusters"));
  const cats = numList(searchParams.get("cats"));
  const subcats = numList(searchParams.get("subs"));
  const actions = numList(searchParams.get("actions"));
  const oppRange = numRange(searchParams.get("opp"));
  const demRange = numRange(searchParams.get("dem"));
  const relRange = numRange(searchParams.get("rel"));
  const fitRange = numRange(searchParams.get("fit"));
  const rankBand = searchParams.get("rank") || "";
  const matched = searchParams.get("matched") || ""; // "yes" | "no" | ""
  const termLen = searchParams.get("len") || "";

  // Tier-2 competitive filters
  const clickRange = numRange(searchParams.get("click"));      // top_click_share_pct
  const convRange = numRange(searchParams.get("conv"));        // top_conversion_share_pct
  const hasComp = searchParams.get("has_comp") || "";          // "yes" | "no" | ""
  const compBrands = numList(searchParams.get("compb"));       // selected competitor brands
  const scopeTab = searchParams.get("scope") || "in";          // "in" | "out" | "all"

  // Generic patcher — pass {key: value|null|[]} and the URL re-syncs.
  const patch = (updates, { resetPage = true } = {}) => {
    const next = new URLSearchParams(searchParams);
    Object.entries(updates).forEach(([k, v]) => {
      if (v === null || v === undefined || v === "" || (Array.isArray(v) && !v.length)) next.delete(k);
      else next.set(k, Array.isArray(v) ? v.join(",") : String(v));
    });
    if (resetPage) next.delete("page");
    setSearchParams(next, { replace: true });
  };

  // ---- Build API params from URL state ----
  const params = useMemo(() => {
    const skip = (page - 1) * PAGE_SIZE;
    const p = new URLSearchParams({ brand_id: selectedBrandId || "", limit: String(PAGE_SIZE), skip: String(skip), sort_by: sort.by, order: sort.order });
    if (search) p.set("search", search);
    if (filter === "new") p.set("only_new", "true");
    if (filter === "rising") p.set("only_rising", "true");
    if (filter === "declining") p.set("only_declining", "true");
    if (filter === "gap") p.set("only_gap", "true");
    if (filter === "high") p.set("min_opportunity", "70");
    clusterIds.forEach((c) => p.append("cluster_ids", c));
    cats.forEach((c) => p.append("categories", c));
    subcats.forEach((c) => p.append("subcategories", c));
    actions.forEach((a) => p.append("actions", a));
    if (oppRange) { p.set("min_opportunity", String(oppRange[0])); p.set("max_opportunity", String(oppRange[1])); }
    if (demRange) { p.set("min_demand", String(demRange[0])); p.set("max_demand", String(demRange[1])); }
    if (relRange) { p.set("min_relevance", String(relRange[0])); p.set("max_relevance", String(relRange[1])); }
    if (fitRange) { p.set("min_fit", String(fitRange[0])); p.set("max_fit", String(fitRange[1])); }
    if (rankBand) p.set("max_rank", rankBand);
    if (matched === "yes") p.set("has_matched_product", "true");
    if (matched === "no") p.set("has_matched_product", "false");
    if (termLen) p.set("term_length", termLen);
    if (clickRange) { p.set("min_click_share", String(clickRange[0])); p.set("max_click_share", String(clickRange[1])); }
    if (convRange) { p.set("min_conv_share", String(convRange[0])); p.set("max_conv_share", String(convRange[1])); }
    if (hasComp === "yes") p.set("has_competitor", "true");
    if (hasComp === "no") p.set("has_competitor", "false");
    compBrands.forEach((b) => p.append("competitor_brands", b));
    if (scopeTab && scopeTab !== "all") p.set("scope", scopeTab);
    return p.toString();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams.toString(), selectedBrandId]);

  useEffect(() => {
    if (!selectedBrandId) return;
    setLoading(true);
    api.get(`/search-terms?${params}`)
      .then((r) => setData(r.data))
      .finally(() => setLoading(false));
  }, [params, selectedBrandId]);

  // Load facets once per brand (lightweight).
  useEffect(() => {
    if (!selectedBrandId) return;
    api.get(`/search-terms/facets?brand_id=${selectedBrandId}`).then((r) => setFacets(r.data || {}));
  }, [selectedBrandId]);

  // Brand-scope counts for the In-scope / Out-of-scope tabs.
  const [scopeCounts, setScopeCounts] = useState({ in: 0, out: 0, all: 0, unclassified: 0 });
  const loadScopeCounts = () => {
    if (!selectedBrandId) return;
    api.get(`/search-terms/scope-counts?brand_id=${selectedBrandId}`)
      .then((r) => setScopeCounts(r.data || { in: 0, out: 0, all: 0, unclassified: 0 }))
      .catch(() => {});
  };
  useEffect(() => { loadScopeCounts(); /* eslint-disable-next-line */ }, [selectedBrandId, data.total]);

  // Manual scope override on a single term.
  const setTermScope = async (term, target) => {
    try {
      await api.patch(`/search-terms/${term.id}/scope`, { scope: target });
      toast.success(target === "auto"
        ? "Reverted to auto-classification"
        : target === "out_of_scope"
          ? "Marked out of brand scope — rematching in background"
          : "Marked back in scope — rematching in background");
      // Refetch the table so the row's relevance_scope updates immediately.
      api.get(`/search-terms?${params}`).then((r) => setData(r.data));
      loadScopeCounts();
    } catch (e) { toast.error("Failed to update scope"); }
  };

  // Cascade subcategory options from selected categories (master taxonomy).
  const subOptions = useMemo(() => {
    if (!cats.length) {
      const all = new Set(facets.subcategories || []);
      return [...all];
    }
    const subs = new Set();
    catMaster.filter((c) => cats.includes(c.category_name)).forEach((c) => (c.subcategories || []).forEach((s) => subs.add(s)));
    (facets.subcategories || []).forEach((s) => subs.add(s));
    return [...subs];
  }, [cats, catMaster, facets.subcategories]);

  const totalPages = Math.max(1, Math.ceil((data.total || 0) / PAGE_SIZE));
  const showingStart = data.items.length ? (page - 1) * PAGE_SIZE + 1 : 0;
  const showingEnd = (page - 1) * PAGE_SIZE + data.items.length;

  const toggleSort = (col) => {
    const next = sort.by === col ? { by: col, order: sort.order === "desc" ? "asc" : "desc" } : { by: col, order: "desc" };
    patch({ sort_by: next.by, order: next.order }, { resetPage: false });
  };

  const setPage = (p) => patch({ page: p === 1 ? null : p }, { resetPage: false });

  // Active filter chips for the strip above the table
  const activeChips = useMemo(() => {
    const out = [];
    if (filter !== "all") out.push({ key: "chip", label: QUICK_FILTERS.find(([k]) => k === filter)?.[1] || filter });
    if (search) out.push({ key: "q", label: `“${search}”` });
    clusterIds.forEach((id) => out.push({ key: `clusters:${id}`, label: facets.clusters.find((c) => c.id === id)?.name || "Cluster" }));
    cats.forEach((c) => out.push({ key: `cats:${c}`, label: c }));
    subcats.forEach((s) => out.push({ key: `subs:${s}`, label: s }));
    actions.forEach((a) => out.push({ key: `actions:${a}`, label: a }));
    if (oppRange) out.push({ key: "opp", label: `Opp ${oppRange[0]}-${oppRange[1]}` });
    if (demRange) out.push({ key: "dem", label: `Demand ${demRange[0]}-${demRange[1]}` });
    if (relRange) out.push({ key: "rel", label: `Relevance ${relRange[0]}-${relRange[1]}` });
    if (fitRange) out.push({ key: "fit", label: `Fit ${fitRange[0]}-${fitRange[1]}` });
    if (rankBand) out.push({ key: "rank", label: `Top ${rankBand}` });
    if (matched === "yes") out.push({ key: "matched", label: "Has matched product" });
    if (matched === "no") out.push({ key: "matched", label: "No matched product" });
    if (termLen) out.push({ key: "len", label: TERM_LENGTH_OPTIONS.find((o) => o.value === termLen)?.label });
    if (clickRange) out.push({ key: "click", label: `Click % ${clickRange[0]}-${clickRange[1]}` });
    if (convRange) out.push({ key: "conv", label: `Conv % ${convRange[0]}-${convRange[1]}` });
    if (hasComp === "yes") out.push({ key: "has_comp", label: "Has competitor" });
    if (hasComp === "no") out.push({ key: "has_comp", label: "No competitor" });
    compBrands.forEach((b) => out.push({ key: `compb:${b}`, label: `vs ${b}` }));
    return out;
  }, [filter, search, clusterIds, cats, subcats, actions, oppRange, demRange, relRange, fitRange, rankBand, matched, termLen, clickRange, convRange, hasComp, compBrands, facets.clusters]);

  const removeChip = (key) => {
    if (key.includes(":")) {
      const [field, val] = key.split(":");
      const map = { clusters: clusterIds, cats, subs: subcats, actions, compb: compBrands };
      patch({ [field]: map[field].filter((v) => v !== val) });
    } else {
      patch({ [key === "chip" ? "chip" : key]: null });
    }
  };

  const clearAll = () => setSearchParams({}, { replace: true });

  // ---------------- Smart Presets ----------------
  // Filter param keys that presets are allowed to touch (everything else — like
  // brand_id from URL or any non-filter context — is preserved).
  const PRESET_KEYS = ["chip", "q", "clusters", "cats", "subs", "actions",
    "opp", "dem", "rel", "fit", "rank", "matched", "len",
    "click", "conv", "has_comp", "compb"];

  const applyPreset = (preset) => {
    const next = new URLSearchParams(searchParams);
    // Clear only filter-related params; preserve brand_id and anything else.
    PRESET_KEYS.forEach((k) => next.delete(k));
    Object.entries(preset.params).forEach(([k, v]) => {
      if (v !== null && v !== undefined && v !== "") next.set(k, String(v));
    });
    setSearchParams(next, { replace: true });
    toast.success(`Preset applied: ${preset.label}`);
  };

  // ---------------- Saved Views ----------------
  const [savedViews, setSavedViews] = useState([]);
  const [saveOpen, setSaveOpen] = useState(false);
  const [saveName, setSaveName] = useState("");
  const [saveShared, setSaveShared] = useState(false);

  const loadSavedViews = () => {
    if (!selectedBrandId) return;
    api.get(`/saved-views?brand_id=${selectedBrandId}&page=search-terms`)
      .then((r) => setSavedViews(r.data || []))
      .catch(() => setSavedViews([]));
  };
  useEffect(() => { loadSavedViews(); /* eslint-disable-next-line */ }, [selectedBrandId]);

  const saveCurrentView = async () => {
    const name = saveName.trim();
    if (!name) return toast.error("View name required");
    try {
      await api.post("/saved-views", {
        name,
        brand_id: selectedBrandId,
        page: "search-terms",
        query_string: searchParams.toString(),
        is_shared: saveShared,
      });
      toast.success(`View "${name}" saved`);
      setSaveOpen(false); setSaveName(""); setSaveShared(false);
      loadSavedViews();
    } catch (e) { toast.error("Failed to save view"); }
  };

  const applyView = (v) => {
    // Replace filter params with the saved view's, but preserve any non-filter
    // params (e.g. brand_id when stored in URL) the user already had.
    const stored = new URLSearchParams(v.query_string || "");
    const next = new URLSearchParams(searchParams);
    [...PRESET_KEYS, "page"].forEach((k) => next.delete(k));
    stored.forEach((val, key) => next.set(key, val));
    setSearchParams(next, { replace: true });
    toast.success(`View "${v.name}" loaded`);
  };

  const deleteView = async (v, e) => {
    e?.stopPropagation();
    if (!window.confirm(`Delete saved view "${v.name}"?`)) return;
    try {
      await api.delete(`/saved-views/${v.id}`);
      toast.success("View deleted");
      loadSavedViews();
    } catch (err) { toast.error("Failed to delete view"); }
  };

  // ---------------- Export ----------------
  const exportRows = (format) => {
    if (!selectedBrandId) return;
    const p = new URLSearchParams(params);
    p.delete("limit"); p.delete("skip");
    p.set("format", format);
    const url = `${API_BASE}/search-terms/export?${p.toString()}`;
    // Auth via fetch + blob (axios on a download link doesn't preserve auth headers).
    fetch(url, { headers: { Authorization: `Bearer ${getToken()}` } })
      .then((r) => {
        if (!r.ok) throw new Error(`HTTP ${r.status}`);
        const cd = r.headers.get("Content-Disposition") || "";
        const m = cd.match(/filename="?([^";]+)"?/);
        const fname = m ? m[1] : `search_terms.${format}`;
        return r.blob().then((b) => ({ b, fname }));
      })
      .then(({ b, fname }) => {
        const a = document.createElement("a");
        a.href = URL.createObjectURL(b);
        a.download = fname;
        document.body.appendChild(a); a.click(); a.remove();
        toast.success(`Exported ${data.total.toLocaleString()} rows as ${format.toUpperCase()}`);
      })
      .catch(() => toast.error("Export failed"));
  };

  const Th = ({ col, label, num }) => (
    <th
      onClick={() => toggleSort(col)}
      className={`px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 cursor-pointer select-none hover:text-slate-900 ${num ? "text-right" : "text-left"}`}>
      {label} {sort.by === col ? (sort.order === "desc" ? "↓" : "↑") : ""}
    </th>
  );

  return (
    <div className="space-y-6">
      <div>
        <p className="text-[11px] uppercase tracking-[0.2em] text-slate-400">Intelligence</p>
        <h1 className="text-3xl font-bold tracking-tight text-slate-900">Search Term Intelligence</h1>
        <p className="mt-1 text-sm text-slate-500">{data.total.toLocaleString()} keywords from latest upload · sorted by {sort.by.replace("_", " ")} · 100 per page.</p>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl shadow-sm">
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3" data-testid="search-terms-toolbar">
          {/* Smart Presets */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="presets-trigger">
                <Sparkles className="h-3.5 w-3.5 text-violet-600" />
                Presets
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-72" align="start">
              <DropdownMenuLabel>Smart presets</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {SMART_PRESETS.map((p) => (
                <DropdownMenuItem
                  key={p.key}
                  data-testid={`preset-${p.key}`}
                  onClick={() => applyPreset(p)}
                  className="flex flex-col items-start py-2 cursor-pointer"
                >
                  <span className="font-semibold">{p.label}</span>
                  <span className="text-[11px] text-slate-500">{p.description}</span>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          {/* Saved Views */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="outline" size="sm" className="h-9 gap-1.5" data-testid="saved-views-trigger">
                <Bookmark className="h-3.5 w-3.5 text-amber-600" />
                Saved views
                {savedViews.length > 0 && <span className="text-[10px] font-bold text-slate-400">({savedViews.length})</span>}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-80" align="start">
              <DropdownMenuLabel>Your saved views</DropdownMenuLabel>
              <DropdownMenuSeparator />
              {!savedViews.length && (
                <div className="px-2 py-3 text-xs text-slate-500">No saved views yet. Configure filters and click "Save view" to create one.</div>
              )}
              {savedViews.map((v) => (
                <DropdownMenuItem
                  key={v.id}
                  data-testid={`saved-view-${v.id}`}
                  onClick={() => applyView(v)}
                  className="flex items-start gap-2 cursor-pointer py-2"
                >
                  <Bookmark className="h-3.5 w-3.5 mt-0.5 text-amber-600 shrink-0" />
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold truncate">{v.name}</div>
                    <div className="text-[10px] text-slate-500 truncate">
                      {v.is_shared && <span className="text-emerald-700 font-semibold mr-1">SHARED</span>}
                      by {v.created_by_name}
                    </div>
                  </div>
                  <button
                    data-testid={`saved-view-delete-${v.id}`}
                    onClick={(e) => deleteView(v, e)}
                    className="rounded p-0.5 text-slate-400 hover:text-rose-600 hover:bg-rose-50 shrink-0"
                  >
                    <Trash2 className="h-3 w-3" />
                  </button>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <Button
            data-testid="save-view-btn"
            variant="outline" size="sm" className="h-9 gap-1.5"
            disabled={!activeChips.length}
            onClick={() => { setSaveOpen(true); setSaveName(""); setSaveShared(false); }}
            title={activeChips.length ? "Save current filters as a view" : "Apply at least one filter to save a view"}
          >
            <BookmarkPlus className="h-3.5 w-3.5" /> Save view
          </Button>

          <div className="flex-1" />

          {/* Export */}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" className="h-9 gap-1.5 bg-slate-900 text-white hover:bg-slate-800" data-testid="export-trigger" disabled={!data.total}>
                <Download className="h-3.5 w-3.5" />
                Export {data.total ? `(${data.total.toLocaleString()})` : ""}
                <ChevronDown className="h-3 w-3 opacity-60" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-56">
              <DropdownMenuLabel>Export filtered rows</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem data-testid="export-csv" onClick={() => exportRows("csv")} className="cursor-pointer">
                <Download className="h-3.5 w-3.5 mr-2" /> Download as CSV
              </DropdownMenuItem>
              <DropdownMenuItem data-testid="export-xlsx" onClick={() => exportRows("xlsx")} className="cursor-pointer">
                <Download className="h-3.5 w-3.5 mr-2" /> Download as Excel (.xlsx)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[240px]">
            <Search strokeWidth={1.5} className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
            <Input data-testid="search-terms-search" value={search} onChange={(e) => patch({ q: e.target.value })} placeholder="Search keywords…" className="pl-9 h-9" />
          </div>
          <div className="flex flex-wrap items-center gap-1.5">
            <Filter strokeWidth={1.5} className="h-3.5 w-3.5 text-slate-400" />
            {[["all", "All"], ...QUICK_FILTERS].map(([k, l]) => (
              <button key={k}
                data-testid={`filter-${k}`}
                onClick={() => patch({ chip: k === "all" ? null : k })}
                className={`rounded-full border px-3 py-1 text-xs font-semibold ${
                  filter === k ? "bg-slate-900 text-white border-slate-900" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>{l}</button>
            ))}
          </div>
        </div>

        {/* Tier-1 filter bar */}
        <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2" data-testid="filter-bar">
          <MultiSelect
            label="Cluster" testid="filter-cluster" placeholder="All"
            options={(facets.clusters || []).map((c) => ({ value: c.id, label: c.name, count: c.count }))}
            value={clusterIds} onChange={(v) => patch({ clusters: v })}
          />
          <MultiSelect
            label="Category" testid="filter-category" placeholder="All"
            options={(catMaster.length ? catMaster.map((c) => c.category_name) : facets.categories || []).map((c) => ({ value: c, label: c }))}
            value={cats} onChange={(v) => patch({ cats: v, subs: null })}
          />
          <MultiSelect
            label="Subcategory" testid="filter-subcategory" placeholder="All"
            options={subOptions.map((s) => ({ value: s, label: s }))}
            value={subcats} onChange={(v) => patch({ subs: v })}
          />
          <MultiSelect
            label="Action" testid="filter-action" placeholder="All"
            options={(facets.actions || []).map((a) => ({ value: a, label: a }))}
            value={actions} onChange={(v) => patch({ actions: v })}
            width="w-[260px]"
          />
          <div className="inline-flex items-center gap-1 border border-slate-200 rounded-md p-0.5" data-testid="filter-rank-band">
            {RANK_BANDS.map(([v, l]) => (
              <button key={v || "all"} onClick={() => patch({ rank: v || null })}
                className={`px-2.5 py-1 text-[11px] font-medium rounded ${rankBand === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 border border-slate-200 rounded-md p-0.5" data-testid="filter-matched">
            <span className="px-2 text-[11px] text-slate-500">Matched</span>
            {[["", "Any"], ["yes", "Yes"], ["no", "No"]].map(([v, l]) => (
              <button key={v || "any"} onClick={() => patch({ matched: v || null })}
                className={`px-2.5 py-1 text-[11px] font-medium rounded ${matched === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <div className="inline-flex items-center gap-1 border border-slate-200 rounded-md p-0.5" data-testid="filter-term-length">
            <span className="px-2 text-[11px] text-slate-500">Length</span>
            {[["", "Any"], ...TERM_LENGTH_OPTIONS.map((o) => [o.value, o.label])].map(([v, l]) => (
              <button key={v || "any"} onClick={() => patch({ len: v || null })}
                className={`px-2.5 py-1 text-[11px] font-medium rounded ${termLen === v ? "bg-slate-900 text-white" : "text-slate-600 hover:bg-slate-50"}`}>
                {l}
              </button>
            ))}
          </div>
          <Button variant="ghost" size="sm" onClick={() => setShowAdvanced((v) => !v)} className="h-9 text-xs gap-1.5" data-testid="filter-toggle-advanced">
            <SlidersHorizontal className="h-3.5 w-3.5" />
            {showAdvanced ? "Hide score sliders" : "Score sliders"}
          </Button>
        </div>

        {showAdvanced && (
          <div className="px-5 py-3 border-b border-slate-100 flex flex-wrap items-center gap-2 bg-slate-50/40" data-testid="filter-bar-advanced">
            <RangeSlider label="Opportunity" value={oppRange} onChange={(v) => patch({ opp: v })} testid="range-opp" />
            <RangeSlider label="Demand" value={demRange} onChange={(v) => patch({ dem: v })} testid="range-dem" />
            <RangeSlider label="Relevance" value={relRange} onChange={(v) => patch({ rel: v })} testid="range-rel" />
            <RangeSlider label="Business fit" value={fitRange} onChange={(v) => patch({ fit: v })} testid="range-fit" />
            <div className="w-full h-px bg-slate-200/60 my-1" />
            <span className="text-[11px] uppercase tracking-wider text-violet-600 font-semibold mr-1">Competitive (Tier-2)</span>
            <RangeSlider label="Click share %" value={clickRange} onChange={(v) => patch({ click: v })} testid="range-click" />
            <RangeSlider label="Conv share %" value={convRange} onChange={(v) => patch({ conv: v })} testid="range-conv" />
            <select
              value={hasComp}
              onChange={(e) => patch({ has_comp: e.target.value || null })}
              data-testid="select-has-comp"
              className="h-9 rounded-md border border-slate-200 bg-white px-3 text-xs"
            >
              <option value="">Competitor: any</option>
              <option value="yes">Has competitor</option>
              <option value="no">No competitor</option>
            </select>
            <MultiSelect
              label="Competitor brands"
              options={(facets.competitor_brands || []).map((c) => ({ value: c.name, label: `${c.name} (${c.count})` }))}
              value={compBrands}
              onChange={(v) => patch({ compb: v })}
              testid="ms-compb"
            />
          </div>
        )}

        {!!activeChips.length && (
          <div className="px-5 py-2.5 border-b border-slate-100 flex flex-wrap items-center gap-1.5 bg-slate-50/30" data-testid="active-filters">
            <span className="text-[11px] uppercase tracking-wider text-slate-500 mr-1">Active:</span>
            {activeChips.map((c) => (
              <span key={c.key} className="inline-flex items-center gap-1 rounded-full bg-slate-900 text-white pl-2.5 pr-1 py-0.5 text-[11px] font-medium" data-testid={`active-chip-${c.key}`}>
                {c.label}
                <button type="button" onClick={() => removeChip(c.key)} className="rounded-full hover:bg-white/20 p-0.5">
                  <X className="h-2.5 w-2.5" />
                </button>
              </span>
            ))}
            <button type="button" onClick={clearAll} className="ml-1 text-[11px] text-slate-600 hover:text-slate-900 underline" data-testid="clear-all-filters">Clear all</button>
          </div>
        )}

        {/* In-scope / Out-of-scope tabs */}
        <div className="px-5 pt-3 pb-0 border-b border-slate-100 flex items-center gap-1" data-testid="scope-tabs">
          {[
            { key: "in", label: "In-scope", count: scopeCounts.in, testid: "scope-tab-in" },
            { key: "out", label: "Out-of-scope", count: scopeCounts.out, testid: "scope-tab-out" },
            { key: "all", label: "All", count: scopeCounts.all, testid: "scope-tab-all" },
          ].map((t) => (
            <button
              key={t.key}
              data-testid={t.testid}
              onClick={() => patch({ scope: t.key === "all" ? null : t.key })}
              className={`px-3.5 py-2 text-xs font-semibold border-b-2 -mb-px transition-colors ${
                scopeTab === t.key
                  ? "border-slate-900 text-slate-900"
                  : "border-transparent text-slate-500 hover:text-slate-900"
              }`}
            >
              {t.label} <span className="ml-1 text-[10px] font-normal text-slate-400">({t.count.toLocaleString()})</span>
            </button>
          ))}
          {scopeCounts.unclassified > 0 && (
            <span className="ml-3 text-[10px] text-amber-700 bg-amber-50 border border-amber-200 rounded-full px-2 py-0.5">
              {scopeCounts.unclassified.toLocaleString()} unclassified — set brand categories to enable auto-flagging
            </span>
          )}
        </div>

        <div className="overflow-x-auto" data-testid="search-terms-table">
          <table className="w-full text-sm">
            <thead className="bg-slate-50 border-b border-slate-200">
              <tr>
                <Th col="search_term" label="Search term" />
                <Th col="search_frequency_rank" label={<LabelWithTip term="RANK">Rank</LabelWithTip>} num />
                <Th col="rank_change" label={<LabelWithTip term="RANK_CHANGE">Δ Rank</LabelWithTip>} num />
                <Th col="estimated_search_volume" label={<LabelWithTip term="EST_VOLUME">Est. volume / wk</LabelWithTip>} num />
                <Th col="top_click_share_pct" label={<LabelWithTip term="CLICK_SHARE">Top click %</LabelWithTip>} num />
                <Th col="top_conversion_share_pct" label={<LabelWithTip term="CONV_SHARE">Top conv %</LabelWithTip>} num />
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-left">
                  <LabelWithTip term="CLUSTER">Cluster</LabelWithTip>
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-left">Matched product</th>
                <Th col="relevance_score" label={<LabelWithTip term="RELEVANCE">Relevance</LabelWithTip>} num />
                <Th col="demand_score" label={<LabelWithTip term="DEMAND">Demand</LabelWithTip>} num />
                <Th col="business_fit_score" label={<LabelWithTip term="FIT">Fit</LabelWithTip>} num />
                <Th col="opportunity_score" label={<LabelWithTip term="OPPORTUNITY">Opportunity</LabelWithTip>} num />
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">
                  <LabelWithTip term="LEARNING_SIGNAL">Ads</LabelWithTip>
                </th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-left">Recommended action</th>
                <th className="px-4 py-3 text-xs font-semibold uppercase tracking-wider text-slate-500 text-right">Scope</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map((t) => (
                <tr key={t.id} className={`border-b border-slate-100 hover:bg-slate-50/50 transition-colors ${t.is_brand_negative ? "opacity-70" : ""}`}>
                  <td className="px-4 py-3">
                    <div className="font-medium text-slate-900">
                      {t.search_term}
                      {t.is_brand_negative && (
                        <span className="ml-1.5 inline-flex items-center rounded-full border border-rose-200 bg-rose-50 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wider text-rose-700">Out of scope</span>
                      )}
                    </div>
                    <div className="flex gap-1 mt-0.5">
                      {t.is_new_keyword && <Tag tone="blue">New</Tag>}
                      {t.is_rising_keyword && <Tag tone="emerald">Rising</Tag>}
                      {t.is_declining_keyword && <Tag tone="rose">Declining</Tag>}
                      {t.is_product_gap && <Tag tone="orange">Gap</Tag>}
                      {t.manual_scope_override && <Tag tone="blue">Manual</Tag>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-600">{t.search_frequency_rank?.toLocaleString() || "—"}</td>
                  <td className="px-4 py-3 text-right font-mono text-xs">
                    {t.rank_change == null ? "—" : (
                      <span className={t.rank_change < 0 ? "text-emerald-700" : t.rank_change > 0 ? "text-rose-700" : "text-slate-500"}>
                        {t.rank_change < 0 ? "▲" : "▼"} {Math.abs(t.rank_change)}
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">
                    {t.estimated_search_volume == null
                      ? <span className="text-slate-400">—</span>
                      : t.estimated_search_volume >= 1000
                        ? `${(t.estimated_search_volume / 1000).toFixed(1)}k`
                        : t.estimated_search_volume.toLocaleString()}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">
                    {t.top_click_share_pct == null ? <span className="text-slate-400">—</span> : `${t.top_click_share_pct.toFixed(1)}%`}
                  </td>
                  <td className="px-4 py-3 text-right font-mono text-xs text-slate-700">
                    {t.top_conversion_share_pct == null ? <span className="text-slate-400">—</span> : `${t.top_conversion_share_pct.toFixed(1)}%`}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600">{t.detected_cluster_name || "—"}</td>
                  <td className="px-4 py-3 text-xs text-slate-700">{t.matched_product_name || <span className="text-slate-400">—</span>}</td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={t.relevance_score} /></td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={t.demand_score} /></td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={t.business_fit_score} /></td>
                  <td className="px-4 py-3 text-right"><ScoreBadge score={t.opportunity_score} /></td>
                  <td className="px-4 py-3 text-right text-xs" data-testid={`ads-cell-${t.id}`}>
                    {t.ad_acos != null || t.ad_spend != null || t.ad_orders != null ? (
                      <div className="flex flex-col items-end leading-tight">
                        <span className={`font-mono font-semibold ${
                          t.ad_acos == null ? "text-slate-400"
                          : t.ad_acos < 25 ? "text-emerald-700"
                          : t.ad_acos < 40 ? "text-amber-700" : "text-rose-700"
                        }`}>
                          {t.ad_acos == null ? "—" : `${t.ad_acos}%`}
                        </span>
                        <span className="text-[10px] text-slate-500">
                          ₹{Math.round(t.ad_spend || 0).toLocaleString("en-IN")} · {t.ad_orders || 0} ord
                        </span>
                      </div>
                    ) : <span className="text-slate-300">—</span>}
                  </td>
                  <td className="px-4 py-3 text-xs text-slate-600 max-w-[260px]">{t.recommended_action || "—"}</td>
                  <td className="px-4 py-3 text-right">
                    {t.is_brand_negative ? (
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`scope-mark-in-${t.id}`}
                        onClick={() => setTermScope(t, "in_scope")}
                        title="Mark as in scope (manual override)"
                        className="text-emerald-700 hover:bg-emerald-50 h-7 px-2 gap-1"
                      >
                        <RotateCcw className="h-3 w-3" /> In
                      </Button>
                    ) : (
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`scope-mark-out-${t.id}`}
                        onClick={() => setTermScope(t, "out_of_scope")}
                        title="Mark as out of brand scope (negative keyword)"
                        className="text-rose-600 hover:bg-rose-50 h-7 px-2 gap-1"
                      >
                        <Ban className="h-3 w-3" /> Out
                      </Button>
                    )}
                    {t.manual_scope_override && (
                      <Button
                        variant="ghost" size="sm"
                        data-testid={`scope-revert-${t.id}`}
                        onClick={() => setTermScope(t, "auto")}
                        title="Revert to auto-classification"
                        className="text-slate-500 hover:bg-slate-100 h-7 px-2 ml-1"
                      >
                        ↺
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
              {!data.items.length && !loading && (
                <tr><td colSpan={15} className="px-4 py-12 text-center text-sm text-slate-500">No keywords found. Upload a CSV to start.</td></tr>
              )}
              {loading && (
                <tr><td colSpan={15} className="px-4 py-12 text-center text-sm text-slate-500">Loading…</td></tr>
              )}
            </tbody>
          </table>
        </div>

        <div className="px-5 py-3 border-t border-slate-100 flex items-center justify-between gap-3 text-xs text-slate-500" data-testid="search-terms-pagination">
          <span>
            {data.total > 0
              ? <>Showing <span className="font-semibold text-slate-700">{showingStart.toLocaleString()}–{showingEnd.toLocaleString()}</span> of <span className="font-semibold text-slate-700">{data.total.toLocaleString()}</span></>
              : "No results"}
          </span>
          <div className="flex items-center gap-1.5">
            <Button
              data-testid="search-terms-prev"
              size="sm" variant="outline"
              disabled={page <= 1 || loading}
              onClick={() => setPage(Math.max(1, page - 1))}
              className="h-7 px-3 text-xs">Prev</Button>
            <span className="px-2 text-slate-500">Page <span className="font-semibold text-slate-700">{page}</span> / {totalPages}</span>
            <Button
              data-testid="search-terms-next"
              size="sm" variant="outline"
              disabled={page >= totalPages || loading}
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              className="h-7 px-3 text-xs">Next</Button>
          </div>
        </div>
      </div>

      {/* Save view dialog */}
      <Dialog open={saveOpen} onOpenChange={setSaveOpen}>
        <DialogContent className="max-w-md" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>Save current filters as a view</DialogTitle>
          </DialogHeader>
          <div className="space-y-3">
            <div>
              <Label className="text-xs font-semibold uppercase tracking-wider text-slate-500 mb-1 block">View name</Label>
              <Input
                data-testid="save-view-name"
                value={saveName}
                onChange={(e) => setSaveName(e.target.value)}
                placeholder="e.g. Sunscreen — Quick Wins"
                onKeyDown={(e) => e.key === "Enter" && saveCurrentView()}
                autoFocus
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border border-slate-200 px-3 py-2.5">
              <div>
                <div className="text-sm font-semibold text-slate-900">Share with team</div>
                <div className="text-[11px] text-slate-500">Other users with this brand can see &amp; apply this view.</div>
              </div>
              <Switch data-testid="save-view-shared" checked={saveShared} onCheckedChange={setSaveShared} />
            </div>
            <div className="text-[11px] text-slate-500">
              {activeChips.length} filter{activeChips.length === 1 ? "" : "s"} captured: {activeChips.map((c) => c.label).filter(Boolean).slice(0, 4).join(", ")}{activeChips.length > 4 ? "…" : ""}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveOpen(false)}>Cancel</Button>
            <Button data-testid="save-view-submit" onClick={saveCurrentView} className="bg-slate-900 text-white hover:bg-slate-800">Save view</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function Tag({ tone, children }) {
  const map = {
    blue: "bg-blue-50 text-blue-700 border-blue-200",
    emerald: "bg-emerald-50 text-emerald-700 border-emerald-200",
    rose: "bg-rose-50 text-rose-700 border-rose-200",
    orange: "bg-orange-50 text-orange-700 border-orange-200",
  };
  return <span className={`inline-flex items-center rounded-full border px-1.5 py-0.5 text-[10px] font-semibold ${map[tone]}`}>{children}</span>;
}
